import { motion } from "framer-motion";
import { PARTNER_BADGES, STATS } from "../data/content";

/* Walmart-style 6-spoke spark */
function Spark({ color }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <rect
          key={deg}
          x="11"
          y="2"
          width="2"
          height="7"
          rx="1"
          fill={color}
          transform={`rotate(${deg} 12 12)`}
        />
      ))}
    </svg>
  );
}

/* amazon-style smile arrow */
function Smile({ color }) {
  return (
    <svg viewBox="0 0 80 16" className="mt-0.5 h-2.5 w-16" aria-hidden="true">
      <path
        d="M4 4 C 24 16, 56 16, 76 4"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M74 8 L78 3 L70 4 Z" fill={color} />
    </svg>
  );
}

const cardVariant = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
};

export default function AuthoritySection() {
  return (
    <section
      className="w-full bg-obsidian px-5 py-16 md:py-20"
      data-testid="authority-section"
    >
      <div className="mx-auto max-w-5xl">
        <p className="text-center text-xs font-bold uppercase tracking-[0.28em] text-neutral-500">
          Official &amp; Verified Partner Status
        </p>

        {/* Badge cards */}
        <div className="mt-10 flex flex-col items-stretch justify-center gap-6 md:flex-row md:gap-0">
          {PARTNER_BADGES.map((b, i) => (
            <div key={b.brand} className="flex items-center md:contents">
              <motion.div
                variants={cardVariant}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                className="flex w-full flex-col justify-center rounded-2xl bg-white px-8 py-7 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.5)] md:mx-6 md:w-64"
                data-testid={`partner-badge-${b.tier.toLowerCase()}`}
              >
                <div className="flex items-end gap-1.5">
                  <span className="text-2xl font-black tracking-tight text-neutral-900">
                    {b.brand}
                  </span>
                  {b.brand === "Walmart" && <Spark color={b.accent} />}
                  {b.sub && (
                    <span className="text-2xl font-medium tracking-tight text-neutral-700">
                      {b.sub}
                    </span>
                  )}
                </div>
                {b.brand === "amazon" && <Smile color={b.accent} />}
                <div className="mt-3">
                  <span
                    className="block text-xl font-extrabold leading-none"
                    style={{ color: b.tierColor }}
                  >
                    {b.tier}
                  </span>
                  <span className="text-xl font-medium text-neutral-900">partner</span>
                </div>
              </motion.div>
              {i < PARTNER_BADGES.length - 1 && (
                <span className="mx-1 hidden h-24 w-px bg-gradient-to-b from-transparent via-indigo-500/40 to-transparent md:block" />
              )}
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="mx-auto mt-16 h-px w-full max-w-4xl bg-white/10" />

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 gap-y-10 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              variants={cardVariant}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.5 }}
              className="flex flex-col items-center px-3 text-center md:border-r md:border-white/10 md:last:border-r-0"
              data-testid={`stat-${i}`}
            >
              <span className="text-5xl font-black tracking-tight text-[#2F5BFF] md:text-6xl">
                {s.value}
              </span>
              <span className="mt-3 max-w-[12rem] text-sm text-neutral-400">
                {s.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
